// Order Management API Service

import api from './api';
import type {
  OrderListResponse,
  OrderDetailResponse,
  ManualOrderRequest,
  TransitionRequest,
  LineItemCreateRequest,
  OrderRecord,
  OrderLineItem,
  OrderStatus,
  OrderSourceType,
  SageTrackingResponse,
} from '../types/orders';

export const orderService = {
  /**
   * List orders with optional filtering, search, and sorting
   */
  listOrders: async (params?: {
    status?: OrderStatus;
    customer_no?: string;
    salesperson_no?: string;
    source_type?: OrderSourceType;
    search?: string;
    date_from?: string;
    date_to?: string;
    exclude_terminal?: boolean;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }): Promise<OrderListResponse> => {
    const response = await api.get<OrderListResponse>('/orders', { params });
    return response.data;
  },

  /**
   * Get order detail with line items and history
   */
  getOrder: async (orderId: string): Promise<OrderDetailResponse> => {
    const response = await api.get<OrderDetailResponse>(`/orders/${orderId}`);
    return response.data;
  },

  /**
   * Create a new manual order
   */
  createOrder: async (data: ManualOrderRequest): Promise<OrderRecord> => {
    const response = await api.post<OrderRecord>('/orders', data);
    return response.data;
  },

  /**
   * Update order fields
   */
  updateOrder: async (
    orderId: string,
    data: Partial<ManualOrderRequest>,
  ): Promise<OrderRecord> => {
    const response = await api.patch<OrderRecord>(`/orders/${orderId}`, data);
    return response.data;
  },

  /**
   * Execute a state transition
   */
  transitionOrder: async (
    orderId: string,
    data: TransitionRequest,
  ): Promise<OrderDetailResponse> => {
    const response = await api.post<OrderDetailResponse>(
      `/orders/${orderId}/transition`,
      data,
    );
    return response.data;
  },

  /**
   * Get order transition history
   */
  getHistory: async (orderId: string): Promise<OrderDetailResponse['history']> => {
    const response = await api.get(`/orders/${orderId}/history`);
    return response.data;
  },

  /**
   * Add a line item to an order
   */
  addLineItem: async (
    orderId: string,
    data: LineItemCreateRequest,
  ): Promise<OrderLineItem> => {
    const response = await api.post<OrderLineItem>(
      `/orders/${orderId}/line-items`,
      data,
    );
    return response.data;
  },

  /**
   * Update a line item
   */
  updateLineItem: async (
    orderId: string,
    itemId: string,
    data: Partial<LineItemCreateRequest>,
  ): Promise<OrderLineItem> => {
    const response = await api.patch<OrderLineItem>(
      `/orders/${orderId}/line-items/${itemId}`,
      data,
    );
    return response.data;
  },

  /**
   * Remove a line item
   */
  removeLineItem: async (orderId: string, itemId: string): Promise<void> => {
    await api.delete(`/orders/${orderId}/line-items/${itemId}`);
  },

  /**
   * Get Sage tracking data (submission response + validation lookup)
   */
  getSageTracking: async (orderId: string): Promise<SageTrackingResponse> => {
    const response = await api.get<SageTrackingResponse>(
      `/orders/${orderId}/sage-tracking`,
    );
    return response.data;
  },
};
